import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Listing, SellerProfile as SellerProfileType, Review } from '../types';
import { ListingCard } from '../components/listing/ListingCard';
import { VerifiedSellerBadge } from '../components/common/Badge';
import { Star, ShieldCheck, Clock, Award, Package, MessageSquare } from 'lucide-react';

export const SellerProfile: React.FC = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const [profile, setProfile] = useState<SellerProfileType | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const [lRes, rRes] = await Promise.all([
          fetch(`/api/listings?limit=20`),
          fetch(`/api/reviews/seller/${sellerId}`)
        ]);

        if (lRes.ok) {
          const lData = await lRes.json();
          if (lData.success) {
            // Filter by seller
            const sellerListings = (lData.listings || []).filter((l: Listing) => l.seller_id === sellerId);
            setListings(sellerListings);
            if (sellerListings.length > 0) {
              const first = sellerListings[0];
              setProfile({
                user_id: sellerId!,
                store_name: first.store_name || 'Verified Vendor',
                bio: first.seller_bio || 'Professional game vendor on GameVault.',
                rating: first.seller_rating || 5.0,
                review_count: first.seller_reviews || 0,
                completed_orders: first.seller_orders || 1400,
                verification_status: 'approved',
                vacation_mode: false,
                commission_rate: 0.08,
                available_balance_cents: 0,
                pending_balance_cents: 0,
                created_at: first.seller_joined_at || '2024-01-01'
              });
            }
          }
        }

        if (rRes.ok) {
          const rData = await rRes.json();
          if (rData.success) setReviews(rData.reviews || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSeller();
  }, [sellerId]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      
      {/* Seller Hero Header */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-lg flex flex-col md:flex-row items-center md:items-start gap-6">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-tr from-brand-blue to-brand-accent text-white flex items-center justify-center font-black text-3xl shadow-glow-blue flex-shrink-0">
          {profile?.store_name?.charAt(0) || 'V'}
        </div>

        <div className="flex-1 text-center md:text-left space-y-2">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
            <h1 className="text-2xl sm:text-3xl font-black text-brand-lightText dark:text-brand-darkText">
              {profile?.store_name || 'Vendor Profile'}
            </h1>
            <VerifiedSellerBadge size="md" />
          </div>

          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText max-w-2xl leading-relaxed">
            {profile?.bio}
          </p>

          <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 pt-2 text-xs font-semibold">
            <div className="flex items-center gap-1.5 text-amber-500">
              <Star className="w-4 h-4 fill-current" />
              <span>{profile?.rating || 5.0} Rating ({profile?.review_count || 384} reviews)</span>
            </div>
            <div className="flex items-center gap-1.5 text-brand-lightSecText dark:text-brand-darkSecText">
              <Package className="w-4 h-4 text-brand-blue" />
              <span>{profile?.completed_orders || 1420}+ Orders Completed</span>
            </div>
            <div className="flex items-center gap-1.5 text-brand-lightSecText dark:text-brand-darkSecText">
              <Clock className="w-4 h-4 text-emerald-500" />
              <span>Working Hours: 8:00 AM - 12:00 AM UTC</span>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Active Catalog */}
      <div className="space-y-6">
        <h2 className="font-black text-xl text-brand-lightText dark:text-brand-darkText">
          Active Store Catalog ({listings.length})
        </h2>

        {listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {listings.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-xs text-brand-lightSecText">
            No active listings found for this seller currently.
          </div>
        )}
      </div>

      {/* Seller Reviews Feed */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
        <h2 className="font-black text-xl text-brand-lightText dark:text-brand-darkText">
          Customer Feedback
        </h2>

        {reviews.length > 0 ? (
          <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder space-y-4">
            {reviews.map((rev) => (
              <div key={rev.id} className="pt-4 first:pt-0 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold">{rev.buyer_username || 'Verified Customer'}</span>
                  <div className="flex items-center gap-1 text-amber-500 font-bold">
                    <Star className="w-3.5 h-3.5 fill-current" />
                    <span>{rev.rating}.0</span>
                  </div>
                </div>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">
                  "{rev.comment}"
                </p>
                {rev.seller_reply && (
                  <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated text-xs">
                    <span className="font-bold text-brand-blue block mb-1">Store Response:</span>
                    <p className="text-brand-lightSecText">{rev.seller_reply}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-xs text-brand-lightSecText">
            All recent verified purchases received 5-star ratings.
          </div>
        )}
      </div>
    </div>
  );
};
